//Camera.tsx
import { OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Camera, Vector3 } from "three";
import { cameraTargetPointSelector } from "../redux/cameraSelectors";
import { cameraActions } from "../redux/cameraSlice";
import { DebugBox } from "./DebugBox";
import { isPointInsideBoundary } from "../utils/isPointInsideBoundary";
import { getClosestPointOnBoundary } from "../utils/getClosestPointOnBoundary";
import { interactionBoundarySpecSelector } from "../redux/interactionSelectors";

type CameraHandlerProps = {
  initialViewPoint?: CameraViewPoint;
  baseBoundary?: BoundarySpec;
}

const animationDuration = 1;

export const CameraHandler = (props: CameraHandlerProps) => {
  const { initialViewPoint, baseBoundary } = props;
  const cameraRef = useRef<Camera>();
  const orbitRef = useRef<any>();
  const cameraTarget = useSelector(cameraTargetPointSelector);
  const animationCounterRef = useRef(0);
  const animationOriginRef = useRef<CameraViewPoint>();
  const animationTargetRef = useRef<CameraViewPoint>();
  const lastCameraPositionRef = useRef<XYZ>();
  const dispatch = useDispatch();
  const [boundary, setBoundary] = useState<BoundarySpec>();
  const interactionBoundarySpec = useSelector(interactionBoundarySpecSelector);

  useEffect(() => {
    // interaction boundary spec always higher priority than the base boundary
    if (interactionBoundarySpec) {
      setBoundary(interactionBoundarySpec);
    } else {
      setBoundary(baseBoundary);
    }
  }, [baseBoundary, interactionBoundarySpec]);

  useEffect(() => {
    const windowAny = window as any;
    windowAny.cameraRef = cameraRef;
    windowAny.orbitRef = orbitRef;
    windowAny.toggleCameraBoundary = () => {
      dispatch(cameraActions.toggleBoundaryDebug())
    }
  }, [dispatch, cameraRef, orbitRef])


  useEffect(() => {
    if (initialViewPoint) {
      if (cameraRef.current) {
        cameraRef.current.position.set(...initialViewPoint.position)
      }
      if (orbitRef.current) {
        orbitRef.current.target.set(...initialViewPoint.target)
      }
    }
  }, [initialViewPoint])

  useThree(({ camera }) => {
    cameraRef.current = camera;
  });

  const validatePosition = () => {
    const camera = cameraRef.current;
    if (!camera) {
      return;
    }
    const cameraPosition = camera.position;
    // there is a bug if the exclusion is not cleanly inside the inclusion, for now just adjust the exclusion to be cleanly inside the inclusion
    if (boundary?.inclusion) {
      const { center, dimension } = boundary.inclusion;
      const isInsideInclusion = isPointInsideBoundary(cameraPosition, center, dimension, true);
      if (!isInsideInclusion) {
        const { x, y, z } = getClosestPointOnBoundary(cameraPosition, center, dimension, false);
        camera.position.set(x, y, z);
      }
    }
    if (boundary?.exclusion) {
      const { center, dimension } = boundary.exclusion;
      const isInsideExclusion = isPointInsideBoundary(cameraPosition, center, dimension, false);
      if (isInsideExclusion) {
        const { x, y, z } = getClosestPointOnBoundary(cameraPosition, center, dimension, true);
        camera.position.set(x, y, z);
      }
    }
  }

  useFrame((state, delta) => {
    const targetPoint = animationTargetRef.current;
    const originPoint = animationOriginRef.current;
    const orbit = orbitRef.current;
    const camera = cameraRef.current;
    if (!camera || !orbit) {
      return;
    }
    if (!targetPoint || !originPoint) {
      validatePosition();
      return;
    }
    animationCounterRef.current += delta;
    const t = Math.min(animationCounterRef.current / animationDuration, 1);
    const interpolatedPosition = new Vector3().lerpVectors(
      new Vector3(...originPoint.position),
      new Vector3(...targetPoint.position),
      t,
    )
    const interpolatedTarget = new Vector3().lerpVectors(
      new Vector3(...originPoint.target),
      new Vector3(...targetPoint.target),
      t,
    )
    camera.position.copy(interpolatedPosition);
    orbit.target.copy(interpolatedTarget);
    if (t === 1) {
      animationTargetRef.current = undefined;
      animationOriginRef.current = undefined;
      animationCounterRef.current = 0;
    }
    lastCameraPositionRef.current = camera.position.toArray();
  })

  useEffect(() => {
    if (!cameraRef.current || !orbitRef.current) {
      return;
    }
    const target = orbitRef.current.target;
    const position = cameraRef.current.position;
    animationCounterRef.current = 0;
    animationOriginRef.current = {
      position: [...position.toArray()],
      target: [...target.toArray() as XYZ]
    }
    if (!cameraTarget) {
      animationTargetRef.current = initialViewPoint;
      return;
    }
    animationTargetRef.current = cameraTarget;

  }, [cameraTarget, cameraRef, orbitRef, initialViewPoint])

  return (
    <>
      <PerspectiveCamera
        makeDefault
        fov={50}
      />
      <OrbitControls
        ref={orbitRef}
        panSpeed={1}
        zoomSpeed={0.35}
      />

      {boundary?.inclusion && <DebugBox
        center={boundary.inclusion.center}
        dimension={boundary.inclusion.dimension}
        color='blue'
      />}
      {boundary?.exclusion && <DebugBox
        center={boundary.exclusion.center}
        dimension={boundary.exclusion.dimension}
        color='red'
      />}

    </>
  );
};
